import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  PaperAirplaneIcon, 
  StopIcon, 
  PhotoIcon, 
  XMarkIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  /*const [streamController, setStreamController] = useState(null);*/
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setLoading(false);
      /*setStreamController(null);*/
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) return;

    // Limit to 5 images
    const limitedFiles = imageFiles.slice(0, 5);
    setSelectedImages(limitedFiles);

    // Create preview URLs
    const previews = limitedFiles.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));
    
    // Clean up old previews
    previewImages.forEach(preview => URL.revokeObjectURL(preview.url));
    setPreviewImages(previews);
  };

  const removeImage = (index) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = previewImages.filter((_, i) => i !== index);
    
    // Clean up the removed preview URL
    URL.revokeObjectURL(previewImages[index].url);
    
    setSelectedImages(newImages);
    setPreviewImages(newPreviews);
  };

  const clearImages = () => {
    previewImages.forEach(preview => URL.revokeObjectURL(preview.url));
    setSelectedImages([]);
    setPreviewImages([]);
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data:image/...;base64, prefix
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!inputMessage.trim() && selectedImages.length === 0) || loading) return;

    // Prepare user message
    const userMessage = {
      content: inputMessage || '[Image(s) uploaded]',
      role: 'user',
      id: Date.now(),
      created_at: new Date().toISOString(),
      images: previewImages.map(p => ({ url: p.url, name: p.name }))
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    const currentImages = selectedImages;
    
    // Clear form
    setInputMessage('');
    setLoading(true);

    // Create abort controller for cancellation
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Prepare request data
      const requestData = {
        content: currentInput || 'Please describe these images.',
        session_id: currentSessionId,
        has_images: currentImages.length > 0
      };

      // Convert images to base64 if present
      if (currentImages.length > 0) {
        const base64Images = await Promise.all(
          currentImages.map(file => convertImageToBase64(file))
        );
        requestData.images = base64Images;
      }

      const response = await fetch(`${API_BASE_URL}/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      let aiMessageContent = "";
      const aiMessageId = Date.now() + 1;
      
      // Add initial AI message
      const initialAiMessage = {
        content: "",
        role: 'assistant',
        id: aiMessageId,
        created_at: new Date().toISOString(),
        streaming: true,
        model_used: currentImages.length > 0 ? 'llava' : 'llama2'
      };
      setMessages(prev => [...prev, initialAiMessage]);

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            
            if (data === '[DONE]') {
              setLoading(false);
              clearImages(); // Clear images after successful send
              return;
            }
            
            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'chunk') {
                aiMessageContent += parsed.content;
                const contentSnapshot = aiMessageContent;
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMessageId 
                    ? { ...msg, content: contentSnapshot }
                    : msg
                ));
              } else if (parsed.type === 'complete') {
                if (!currentSessionId && parsed.session_id) {
                  setCurrentSessionId(parsed.session_id);
                }
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMessageId 
                    ? { ...msg, streaming: false }
                    : msg
                ));
              } else if (parsed.type === 'error') {
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMessageId 
                    ? { ...msg, content: `Error: ${parsed.content}`, streaming: false }
                    : msg
                ));
              }
            } catch (parseError) {
              console.error('Failed to parse streaming data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Streaming aborted by user');
        setMessages(prev => prev.map(msg => 
          msg.id === (Date.now() + 1) 
            ? { ...msg, content: msg.content + " [Interrupted]", streaming: false }
            : msg
        ));
      } else {
        console.error('Failed to send message:', error);
        const errorMessage = {
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          role: 'assistant',
          id: Date.now() + 1,
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
      clearImages();
    }
  };

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      previewImages.forEach(preview => URL.revokeObjectURL(preview.url));
    };
  }, [previewImages]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <h1 className="text-2xl font-bold text-gray-900">Chat with Jarvis AI</h1>
        <p className="text-sm text-gray-600">
          Powered by Ollama • {currentSessionId ? `Session ${currentSessionId}` : 'New conversation'}
          {selectedImages.length > 0 && (
            <span className="ml-2 text-blue-600">
              📷 Vision Mode (LLaVA)
            </span>
          )}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🤖</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Hello, {user?.full_name || user?.username}!
              </h3>
              <p className="text-gray-600 mb-4">
                I'm Jarvis, your AI assistant. I can help with text and images!
              </p>
              <div className="text-sm text-gray-500">
                <p>I can help you with:</p>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Questions & Research</span>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Image Analysis</span>
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">Visual Description</span>
                  <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">OCR & Text Recognition</span>
                </div>
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-2xl px-4 py-3 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
                }`}
              >
                {/* Images in user message */}
                {message.images && message.images.length > 0 && (
                  <div className="mb-3">
                    <div className="grid grid-cols-2 gap-2">
                      {message.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img
                            src={image.url}
                            alt={image.name}
                            className="w-full h-20 object-cover rounded border"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b truncate">
                            {image.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="prose prose-sm max-w-none">
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  {message.streaming && (
                    <div className="flex items-center mt-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`text-xs mt-2 flex items-center justify-between ${
                  message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  <span>{new Date(message.created_at).toLocaleTimeString()}</span>
                  {message.model_used && (
                    <span className={`px-2 py-1 rounded text-xs ${
                      message.role === 'user' 
                        ? 'bg-blue-500 text-blue-100' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {message.model_used}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Image Preview */}
      {previewImages.length > 0 && (
        <div className="bg-white border-t border-gray-200 px-6 py-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Selected Images ({previewImages.length}/5)
              </span>
              <button
                onClick={clearImages}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Clear All
              </button>
            </div>
            <div className="flex space-x-2 overflow-x-auto">
              {previewImages.map((preview, index) => (
                <div key={index} className="relative flex-shrink-0">
                  <img
                    src={preview.url}
                    alt={preview.name}
                    className="w-16 h-16 object-cover rounded border"
                  />
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 text-xs hover:bg-red-600"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b truncate">
                    {preview.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <form onSubmit={sendMessage} className="max-w-4xl mx-auto">
          <div className="flex space-x-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              accept="image/*"
              multiple
              className="hidden"
            />
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              <PhotoIcon className="w-6 h-6" />
            </button>

            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={
                selectedImages.length > 0 
                  ? "Ask about the images..." 
                  : loading 
                    ? "Jarvis is responding..." 
                    : "Ask me anything..."
              }
              className="flex-1 input-field"
              disabled={loading}
            />
            
            {loading ? (
              <button
                type="button"
                onClick={stopStreaming}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
              >
                <StopIcon className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!inputMessage.trim() && selectedImages.length === 0}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            )}
          </div>
          {loading && (
            <div className="text-xs text-gray-500 mt-2 text-center">
              {selectedImages.length > 0 ? (
                <span>🔍 Analyzing images with LLaVA... Click stop to interrupt</span>
              ) : (
                <span>Streaming response... Click stop to interrupt</span>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}